import React from 'react';
import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import style from './style.module.scss';
import LevelArrowIcon from '@ping/assets/Icon/levelArrow.svg';

import TrendingIcon from '@ping/assets/Icon/trending.svg';
import BarChartIcon from '@ping/assets/Icon/bar-chart.svg';
import SwapIcon from '@ping/assets/Icon/swap.svg';
import NoteBookIcon from '@ping/assets/Icon/noteBook.svg';
import PortfolioIcon from '@ping/assets/Icon/portfolio.svg';
import ReversedArrowsIcon from '@ping/assets/Icon/reversedArrows.svg';

import { useDesktopDevice, useIsUserLoggedIn } from '@ping/hooks';
import { t } from '@ping/helpers';
import { PORTFOLIO_PATH } from '@ping/constants';
import { APP_VERSION } from '@ping/configs';
import { ProfileDropdownMenu, GetStartedButton } from '@ping/components/Header';
import { ExchangeTypeDropdown } from '../Header/ExchangeTypeDropdown';
import { LinkWithDropdown } from '@ping/components/Header/LinkWithDropdown';
import clsx from 'clsx';
import PingCommoditiesLogo from './PingCommoditiesLogo';

type NavLinkHref = LinkProps['href'];

interface NavLink {
  title: string;
  href: NavLinkHref;
  icon: JSX.Element;
  protected?: boolean;
}

interface NavDropdownLink extends NavLink {
  subtitle: string;
}

const primaryNavLinks: NavLink[] = [
  {
    title: 'Dashboard',
    href: '/commodities',
    icon: <TrendingIcon className={style['nav-icon']} />,
  },
];

const marketsNavLink: NavLink = {
  title: 'Markets',
  href: '/markets',
  icon: <BarChartIcon className={style['nav-icon']} />,
};

const tradingDropdownLinks: NavDropdownLink[] = [
  {
    title: 'Simple Trading',
    href: '/commodities/simple-trading',
    icon: <SwapIcon className={style['nav-icon']} />,
    subtitle: 'Place guided orders quickly',
  },
  {
    title: 'Advanced Trading',
    href: '/trade',
    icon: <NoteBookIcon className={style['nav-icon']} />,
    subtitle: 'Access the full trading terminal',
  },
];

const portfolioDropdownLinks: NavDropdownLink[] = [
  {
    title: 'Portfolio',
    href: '/commodities/portfolio',
    icon: <PortfolioIcon className={style['nav-icon']} />,
    subtitle: 'View balances and performance',
    protected: true,
  },
  {
    href: {
      pathname: PORTFOLIO_PATH,
      query: {
        modal: 'send_receive',
        tab: 'send',
      },
    },
    title: t('Send & Receive'),
    subtitle: t('Send or receive assets easily'),
    icon: <ReversedArrowsIcon />,
  },
  {
    href: '/user/portfolio/ramp',
    title: 'Buy & Sell',
    icon: <LevelArrowIcon />,
    subtitle: 'Move funds with Ramp',
    protected: true,
  },
];

export const PingCommoditiesNavbar: React.FC = () => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const router = useRouter();
  const { pathname } = router;

  const resolveHrefPath = (href: NavLinkHref) => {
    if (typeof href === 'string') {
      return href;
    }

    if ('pathname' in href && typeof href.pathname === 'string') {
      return href.pathname;
    }

    return '';
  };

  const filterProtectedLinks = <T extends NavLink>(links: T[]) =>
    links.filter(link => !(link.protected && !isUserLoggedIn));
  const isDesktop = useDesktopDevice();

  const visiblePrimaryLinks = filterProtectedLinks(primaryNavLinks);
  const visibleTradingLinks = filterProtectedLinks(tradingDropdownLinks);
  const visiblePortfolioLinks = filterProtectedLinks(portfolioDropdownLinks);
  const isTradingPathActive = tradingDropdownLinks.some(link => pathname.startsWith(resolveHrefPath(link.href)));
  const isPortfolioPathActive = portfolioDropdownLinks.some(link => pathname.startsWith(resolveHrefPath(link.href)));

  return (
    <nav className={clsx(style['nasdaq-navbar'], style['nav-user'])} data-testid='nasdaq-navbar'>
      <div className={style['navbar-content']}>
        <div className={style['brand']}>
          <a href='/'>
            <div className={style['brand-area']}>
              <div className={style['logo-badge-wrapper']}>
                <PingCommoditiesLogo height={isDesktop ? 32 : 44} onlyIcon={!isDesktop} />
                <img
                  className={style['app-stage-badge']}
                  src={`/img/${APP_VERSION.toLowerCase()}-badge.svg`}
                  alt={`${APP_VERSION.toLowerCase()} badge`}
                />
              </div>
              <div className={style['brand-text-v']}>
                <span>{t('Digital Commodities')}</span>
                <span>{t('Exchange')}</span>
              </div>
            </div>
          </a>
          <ExchangeTypeDropdown />
        </div>
        {isDesktop && (
          <div className={style['links']}>
            {isDesktop &&
              visiblePrimaryLinks.map(link => {
                const hrefPath = resolveHrefPath(link.href);
                const isActive = hrefPath ? pathname.startsWith(hrefPath) : false;

                return (
                  <Link key={hrefPath || link.title} href={link.href} legacyBehavior passHref>
                    <a className={isActive ? style['active'] : undefined}>
                      {link.icon}
                      <span>{link.title}</span>
                    </a>
                  </Link>
                );
              })}
            {isDesktop && visibleTradingLinks.length > 0 && (
              <LinkWithDropdown
                title='Trade'
                disabled={false}
                link={visibleTradingLinks}
                className={clsx(style['dropdown'], { [style['dropdown-active']]: isTradingPathActive })}
                dropdownId='commodities-nav-trade-dropdown'
                dataTestId='commodities-trade-dropdown'
              />
            )}
            {isDesktop && (
              <Link href={marketsNavLink.href} legacyBehavior passHref>
                <a className={pathname.startsWith('/markets') ? style['active'] : undefined}>
                  {marketsNavLink.icon}
                  <span>{marketsNavLink.title}</span>
                </a>
              </Link>
            )}
            {isDesktop && visiblePortfolioLinks.length > 0 && (
              <LinkWithDropdown
                title='Portfolio'
                disabled={false}
                link={visiblePortfolioLinks}
                className={clsx(style['dropdown'], { [style['dropdown-active']]: isPortfolioPathActive })}
                dropdownId='commodities-nav-portfolio-dropdown'
                dataTestId='commodities-portfolio-dropdown'
              />
            )}
          </div>
        )}
        <div className={style['actions']}>
          {isUserLoggedIn ? <ProfileDropdownMenu /> : <GetStartedButton data-testid='login-button-nav' />}
        </div>
      </div>
    </nav>
  );
};
